import styles from './EquipmentCard.module.scss';
import classNames from 'classnames';
import { Picture } from 'ui/components';
import { Link } from 'ui/components/1-atoms';
import StruersEmblemIcon from 'ui/icons/icon-struers-emblem.svg';
import CompanyLogoGrey from 'ui/icons/icon-struers-ametek-grey-logo.svg';
import { Paragraph } from 'ui/components/1-atoms';
import { useContext } from 'react';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { getEquipmentErrorMessage, isMethodHidden, isValidStepData } from 'helpers/equipmentError';
import { formatDuration } from 'helpers/date';

export interface EquipmentCardProps {
	className?: string;
	columnWidth: Models.GridCellWidth;
	equipment: Models.Equipment;
	imageSizes: string;
}

export const EquipmentCard: React.FC<EquipmentCardProps> = ({ className, equipment, imageSizes }) => {
	const dictionary = useContext(DictionaryContext);

	// Extract step data from liveMachineState for running state
	const currentStep = equipment.liveMachineState?.currentStep;
	const totalSteps = equipment.liveMachineState?.totalSteps;
	const hasStepData = isValidStepData(currentStep, totalSteps);
	const stepStatusText = dictionary.getValue('SmartLab.Step$CurrentstepOf$Totalstep', [
		{ name: 'currentstep', value: String(currentStep ?? 0) },
		{ name: 'totalstep', value: String(totalSteps ?? 0) },
	]);

	const errorMessage = getEquipmentErrorMessage(equipment.liveMachineState?.errorText, dictionary, equipment.liveMachineState?.model);
	const infoLabel = dictionary?.getValue('SmartLab.EquipmentCardInfo', null, 'Info:');

	// Priority: error > method-hidden > paused/waiting > running — mutually exclusive by construction
	const renderEquipmentStatus = () => {
		if (errorMessage) {
			return (
				<>
					<Paragraph style="sm" variant="website" className={styles.EquipmentCard_labels}>
						{infoLabel}
					</Paragraph>
					<Paragraph
						style="sm"
						variant="website"
						className={errorMessage.type === 'error' ? styles.EquipmentCard_errorMessage : styles.EquipmentCard_warningMessage}
					>
						{`${errorMessage.prefix}. ${errorMessage.description}`}
					</Paragraph>
				</>
			);
		}

		if (isMethodHidden(equipment.jobStatus)) {
			return (
				<Paragraph style="sm" variant="website" className={styles.EquipmentCard_labels}>
					{infoLabel}
				</Paragraph>
			);
		}

		if (equipment.jobStatus === 'paused' || equipment.jobStatus === 'waiting') {
			return (
				<Paragraph style="sm" variant="website" className={classNames(styles.EquipmentCard_labels, styles.EquipmentCard_labels___truncated)}>
					{equipment.liveMachineState?.methodName || dictionary?.getValue('SmartLab.ManualMode', null, 'Manual mode')}
				</Paragraph>
			);
		}

		if (equipment.jobStatus === 'running') {
			const estimatedTimeRemaining = formatDuration(equipment.liveMachineState?.methodEstFinishedIn);
			return (
				<>
					<Paragraph
						style="sm"
						variant="website"
						className={classNames(styles.EquipmentCard_labels, styles.EquipmentCard_labels___truncated)}
					>
						{equipment.liveMachineState?.methodName || dictionary?.getValue('SmartLab.ManualMode', null, 'Manual mode')}
					</Paragraph>
					{(hasStepData || equipment.jobStep) && <Paragraph style="sm">{hasStepData ? stepStatusText : equipment.jobStep}</Paragraph>}
					{estimatedTimeRemaining && (
						<Paragraph style="sm" variant="website">
							{estimatedTimeRemaining} {dictionary.getValue('SmartLab.Remaining', null, 'remaining')}
						</Paragraph>
					)}
				</>
			);
		}

		return null;
	};

	return (
		<section className={classNames(styles.EquipmentCard, className)}>
			{equipment?.serviceContract && <StruersEmblemIcon className={styles.EquipmentCard_emblemIcon} />}
			<Link className={styles.EquipmentCard_link} url={equipment?.link}>
				<div className={styles.EquipmentCard_image}>
					<div className={styles.EquipmentCard_imageBox}>
						<Picture
							sizes={imageSizes}
							url={equipment?.picture}
							aspectRatio={0.75}
							className={styles.EquipmentCard_imageElement}
							fallback={<CompanyLogoGrey aria-hidden="true" focusable="false" />}
						/>
					</div>
				</div>

				<div className={styles.EquipmentCard_details}>
					<p className={styles.EquipmentCard_category}>{equipment?.categories?.length && equipment?.categories[0]}</p>
					<p className={styles.EquipmentCard_name}>{equipment?.prettyName}</p>
					<p className={styles.EquipmentCard_serialnumber}>
						{dictionary?.getValue('SmartLab.EquipmentNumberLabel', null, 'S/N:')} {equipment?.serialnumber}
					</p>
					<div className={styles.EquipmentCard_bottom}>
						<div className={classNames(styles.EquipmentCard_state)}>{equipment?.state}</div>
						{equipment.userMachineState?.toLowerCase() === 'outoforder' ? (
							<>
								<Paragraph style="sm" variant="website" className={styles.EquipmentCard_labels}>
									{infoLabel}
								</Paragraph>
								<Paragraph style="sm" variant="website" className={styles.EquipmentCard_errorMessage}>
									{dictionary?.getValue('SmartLab.ManuallySetOutOfOrder', null, 'Manually set as out-of-order')}
								</Paragraph>
							</>
						) : (
							renderEquipmentStatus()
						)}
					</div>
				</div>
			</Link>
		</section>
	);
};
